/**
 * Learner preview: group composite questions by sidebar sections (title + rich description + items).
 */

import * as React from 'react';
import { cn, RichTextViewer } from '@ai-content-hub/content-kit';
import map from 'lodash/map';
import {
    buildQuestionSidebarExerciseGroups,
    resolvedQuestionsSidebarRows,
    tiptapDocToPlainText,
} from '../editor/compositeQuestionSidebarRows';
import type { CompositeQuestionsSidebarRow } from '../schema/compositeQuestionLayout';

export interface CompositePlayerGroupedItemsRenderContext {
    nested: boolean;
}

export interface CompositePlayerGroupedItemsBodyProps {
    /** BEM-style prefix for semantic classes, e.g. `ordering-content-player` */
    prefix: string;
    itemIds: readonly string[];
    questionsSidebarRows?: CompositeQuestionsSidebarRow[] | null;
    renderItem: (itemId: string, ctx: CompositePlayerGroupedItemsRenderContext) => React.ReactNode;
}

const sectionRichTextClass = (prefix: string) =>
    cn(
        `${prefix}-section-rich-text`,
        '[&_p]:text-foreground [&_li]:text-foreground',
        '[&_span]:text-foreground [&_strong]:text-foreground'
    );

export function CompositePlayerGroupedItemsBody({
    prefix,
    itemIds,
    questionsSidebarRows,
    renderItem,
}: CompositePlayerGroupedItemsBodyProps): React.ReactNode {
    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(questionsSidebarRows, itemIds),
        [questionsSidebarRows, itemIds]
    );
    const groups = React.useMemo(
        () => buildQuestionSidebarExerciseGroups(sidebarRows),
        [sidebarRows]
    );

    if (itemIds.length === 0 || groups.length === 0) {
        return null;
    }

    if (groups.length === 1 && groups[0].kind === 'root') {
        return (
            <div className={cn(`${prefix}-root`, 'flex flex-col gap-8')}>
                {map(groups[0].exerciseIds, (id) => renderItem(id, { nested: false }))}
            </div>
        );
    }

    return (
        <div className={cn(`${prefix}-body-grouped`, 'flex flex-col gap-10')}>
            {map(groups, (group, gi) => {
                if (group.kind === 'root') {
                    return (
                        <div
                            key={`${prefix}-root-${gi}`}
                            className={cn(`${prefix}-root`, 'flex flex-col gap-8')}
                        >
                            {map(group.exerciseIds, (id) => renderItem(id, { nested: false }))}
                        </div>
                    );
                }
                const showHeader =
                    Boolean(group.section.title.trim()) ||
                    Boolean(tiptapDocToPlainText(group.section.description));
                return (
                    <div
                        key={group.section.id}
                        className={cn(`${prefix}-section`, 'flex flex-col gap-4')}
                    >
                        {showHeader ? (
                            <div className={cn(`${prefix}-section-header`, 'space-y-2')}>
                                {group.section.title.trim() ? (
                                    <h3
                                        className={cn(
                                            `${prefix}-section-title`,
                                            'text-lg font-semibold text-foreground'
                                        )}
                                    >
                                        {group.section.title}
                                    </h3>
                                ) : null}
                                {tiptapDocToPlainText(group.section.description) ? (
                                    <div
                                        className={cn(
                                            `${prefix}-section-description`,
                                            'text-sm text-foreground min-w-0'
                                        )}
                                    >
                                        <RichTextViewer
                                            data={group.section.description}
                                            className={sectionRichTextClass(prefix)}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <div
                            className={cn(`${prefix}-section-questions`, 'flex flex-col gap-8')}
                        >
                            {map(group.exerciseIds, (id) => renderItem(id, { nested: false }))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export interface UseCompositePlayerSidebarLayoutResult {
    sidebarRows: CompositeQuestionsSidebarRow[];
    nestedByItemId: Map<string, boolean>;
    hasSectionGroups: boolean;
}

export function useCompositePlayerSidebarLayout(
    itemIds: readonly string[],
    questionsSidebarRows?: CompositeQuestionsSidebarRow[] | null
): UseCompositePlayerSidebarLayoutResult {
    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(questionsSidebarRows, itemIds),
        [questionsSidebarRows, itemIds]
    );
    const groups = React.useMemo(
        () => buildQuestionSidebarExerciseGroups(sidebarRows),
        [sidebarRows]
    );
    const nestedByItemId = React.useMemo(() => {
        const m = new Map<string, boolean>();
        for (const g of groups) {
            for (const id of g.exerciseIds) {
                m.set(id, false);
            }
        }
        return m;
    }, [groups]);
    const hasSectionGroups = React.useMemo(() => groups.some((g) => g.kind === 'section'), [groups]);
    return { sidebarRows, nestedByItemId, hasSectionGroups };
}
